import { FC, ReactNode, useCallback, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { resolveTenantDomain, fetchTenantConfig, type TTenantConfig } from '@glomopay/tenant';
import Spinner from '@glomopay/shadcn/spinner';

import { TenantContext } from '../contexts/tenant.context';
import { TenantErrorScreen } from '../components/tenant-error-screen';

import { AppErrorCode } from '@/features/analytics/public';
import { publicApiClient } from '@/shared/api-client/public';
import { logger } from '@/lib/logger';

export const TenantProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const domain = resolveTenantDomain();

  const {
    data: tenant,
    isPending,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['tenant', domain],
    queryFn: async () => {
      logger.debug('tenant resolve started', { tenantDomain: domain });
      try {
        const config = await fetchTenantConfig(publicApiClient, domain);
        logger.info('tenant resolved', {
          tenantDomain: domain,
          isWhitelabel: config.isWhitelabel,
          appName: config.branding.appName,
        });
        return config;
      } catch (error) {
        logger.error('tenant resolution failed', {
          tenantDomain: domain,
          reason: 'fetch_error',
          error: error instanceof Error ? error.message : String(error),
        });
        throw error;
      }
    },
  });

  const isRejected = !isPending && !isError && !!tenant && !tenant.isWhitelabel;

  useEffect(() => {
    if (isRejected) logger.error('tenant resolution failed', { tenantDomain: domain, reason: 'not_whitelabel' });
  }, [isRejected, domain]);

  const retry = useCallback(() => {
    void refetch();
  }, [refetch]);

  const value = useMemo(() => ({ tenant: tenant as TTenantConfig, retry }), [tenant, retry]);

  if (isPending) {
    return (
      <div className='bg-bg-default flex min-h-dvh items-center justify-center'>
        <Spinner />
      </div>
    );
  }

  if (isError || !tenant) return <TenantErrorScreen errorCode={AppErrorCode.TENANT_UNAVAILABLE} onRetry={retry} />;

  // A non-whitelabel domain isn't a configured portal — show the error screen rather
  // than render Glomo's default branding on an AMC domain.
  if (!tenant.isWhitelabel) return <TenantErrorScreen errorCode={AppErrorCode.TENANT_NOT_WHITELABEL} onRetry={retry} />;

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
};
